import { useCallback, useState } from 'react';
import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckmarkFill, CopyOutline } from '@lyearn/icons';
import { Button, IconButton, Input, Stack, Typography } from '@lyearn/ui';
import { useAppConfig } from '@/hooks/useAppConfig';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import { usePublishToWebMutation } from '@/modules/corporate/modules/article/graphql/mutation/generated/publishToWeb';

export const TEMP_URL = 'https://meet.lyearn.com/blogs/test-article-123-679b4c3a8582d1874f49bee5';

const SharePublicly = ({ entityId }: { entityId?: string }) => {
  const {
    site: { appUrl },
  } = useAppConfig();
  const { t } = useTranslation(['Article']);
  const [_, publishToWeb] = usePublishToWebMutation();
  const [isPublishing, setIsPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const { onCopy: copyToClipboard } = useCopyToClipboard();
  const [isCopied, { toggle: toggleCopied }] = useToggle(false);
  const onPublish = useCallback(() => {
    if (publicUrl) {
      //revoke access confirmation dialog
    }

    if (entityId) {
      publishToWeb({ data: { entityId, publishToWeb: true } })
        .then((res) => {
          const content: any = res?.data?.publishToWeb?.content;
          if (content?.publicAccessConfig?.publicId && content?.name) {
            const slug = content.name
              .replace(/[^\w\s-]/g, '') //remove special characters
              .replace(/\s+/g, '-') //replace spaces with '-'
              .toLowerCase() //convert to lowercase
              .trim() //remove leading and trailing whitespace
              .replace(/-+/g, '-'); //replace multiple '-' with a single '-'
            const parsedPublicUrl = appUrl
              ? new URL(`/blogs/${slug}-${content.publicId}`, appUrl).href
              : null;
            setPublicUrl(parsedPublicUrl);
          }
          setIsPublishing(false);
        })
        .catch((e) => {
          setIsPublishing(false);
        });
    }
  }, [publicUrl, entityId, publishToWeb, appUrl]);

  const onCopy = useCallback(() => {
    copyToClipboard(publicUrl || TEMP_URL);
    toggleCopied();
    setTimeout(() => {
      toggleCopied();
    }, 3000);
  }, [copyToClipboard, publicUrl, toggleCopied]);

  return (
    <Stack direction="col" gapY={12} justifyContent="center">
      <Typography className="text-center" variant="heading-s">
        {publicUrl ? t('Article:ContentPubliclyAccessible') : t('Article:PublishToWeb')}
      </Typography>
      {publicUrl || true ? (
        <Input
          fullWidth
          readOnly
          appearance="filled"
          endAdornment={
            <>
              {isCopied ? (
                <CheckmarkFill className="h-24 w-24 cursor-pointer" fillColorToken="icon-primary" />
              ) : (
                <IconButton appearance="blur" shape="square" onClick={onCopy}>
                  <CopyOutline className="h-24 w-24 cursor-pointer" fillColorToken="icon-primary" />
                </IconButton>
              )}
            </>
          }
          size="large"
          value={publicUrl || TEMP_URL}
        />
      ) : (
        <Typography className="text-center text-text-secondary" variant="body-short-02">
          {publicUrl
            ? t('Article:ContentPubliclyAccessibleDescription')
            : t('Article:PublishToWebDescription')}
        </Typography>
      )}
      <Button
        fullWidth
        className="mt-24"
        loading={isPublishing}
        shape="square"
        size="large"
        onClick={onPublish}>
        {publicUrl ? t('Article:Unpublish') : t('Article:MakePublic')}
      </Button>
    </Stack>
  );
};

export default SharePublicly;
